import { Injectable } from '@angular/core';
import { HttpService } from '../app/utility/http-service';

/**
 * Developer-only authoring API for data policies (the record-viewer configuration).
 * Mirrors WellChron.Server.Features.DataPolicyAdminController.
 */

// ---- Metadata ----

export interface ColumnMetadata {
  /** FieldModel.Id — links the column back to its canonical field. */
  id: string;
  name: string;
  displayName: string;
  dataType: string;
  isNullable: boolean;
}

/** A foreign key available between two tables, offered as a Link Alias choice for a navigation. */
export interface NavigationAliasOption {
  alias: string;
  displayName: string;
}

// ---- Policy list / detail ----

export interface DataPolicyListItem {
  id: string;
  clientId: string;
  name: string;
  canEdit: boolean;
  isEnabled: boolean;
  rootTablePolicyId: string;
  rootSchema?: string | null;
  rootTable?: string | null;
}

export interface FieldDetail {
  id: string;
  fieldName: string;
  canEdit: boolean;
  sequence: number;
}

export interface TablePolicyDetail {
  id: string;
  schemaName: string;
  tableName: string;
  canEditRecord: boolean;
  canAddRecord: boolean;
  canDeleteRecord: boolean;
  isRoot: boolean;
  fields: FieldDetail[];
}

/** 0 = OneToOne, 1 = OneToMany (matches ForeignKeyCardinality). */
export enum NavigationCardinality {
  OneToOne = 0,
  OneToMany = 1,
}

export interface NavigationDetail {
  id: string;
  parentTablePolicyId: string;
  referenceTablePolicyId: string;
  linkAlias: string;
  cardinality: number;
  sequence: number;
}

export interface DataPolicyDetail {
  id: string;
  clientId: string;
  name: string;
  canEdit: boolean;
  isEnabled: boolean;
  rootTablePolicyId: string;
  tables: TablePolicyDetail[];
  navigations: NavigationDetail[];
}

// ---- Requests ----

export interface CreateDataPolicyRequest {
  name: string;
  canEdit: boolean;
  rootSchemaName: string;
  rootTableName: string;
  rootCanEditRecord: boolean;
  rootCanAddRecord: boolean;
  rootCanDeleteRecord: boolean;
}

export interface UpdateDataPolicyRequest {
  id: string;
  name: string;
  canEdit: boolean;
}

export interface AddTablePolicyRequest {
  dataPolicyId: string;
  schemaName: string;
  tableName: string;
  canEditRecord: boolean;
  canAddRecord: boolean;
  canDeleteRecord: boolean;
}

export interface UpdateTablePolicyRequest {
  tablePolicyId: string;
  canEditRecord: boolean;
  canAddRecord: boolean;
  canDeleteRecord: boolean;
}

export interface AddFieldRequest {
  tablePolicyId: string;
  fieldName: string;
  canEdit: boolean;
  sequence: number;
}

export interface UpdateFieldRequest {
  fieldId: string;
  canEdit: boolean;
  sequence: number;
}

export interface AddNavigationRequest {
  dataPolicyId: string;
  parentTablePolicyId: string;
  referenceTablePolicyId: string;
  linkAlias: string;
  cardinality: number;
  sequence: number;
}

export interface UpdateNavigationRequest {
  navigationId: string;
  linkAlias: string;
  cardinality: number;
  sequence: number;
}

interface IdResult { id: string; }

@Injectable({ providedIn: 'root' })
export class DataPolicyAdminService {
  private readonly base = 'api/data-policy-admin';

  constructor(private readonly http: HttpService) { }

  // ---- Metadata (schema introspection for the dropdowns) ----

  getSchemas(onSuccess: (schemas: string[]) => void): void {
    this.http.get<string[]>(`${this.base}/metadata/schemas`, onSuccess);
  }

  getTables(schema: string, onSuccess: (tables: string[]) => void): void {
    this.http.get<string[]>(
      `${this.base}/metadata/${encodeURIComponent(schema)}/tables`,
      onSuccess
    );
  }

  getColumns(schema: string, table: string, onSuccess: (columns: ColumnMetadata[]) => void): void {
    this.http.get<ColumnMetadata[]>(
      `${this.base}/metadata/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/columns`,
      onSuccess
    );
  }

  getNavigationAliases(
    parentSchema: string, parentTable: string,
    referenceSchema: string, referenceTable: string,
    cardinality: number,
    onSuccess: (options: NavigationAliasOption[]) => void
  ): void {
    const params = new URLSearchParams({
      parentSchema, parentTable, referenceSchema, referenceTable,
      cardinality: String(cardinality),
    });
    this.http.get<NavigationAliasOption[]>(`${this.base}/metadata/navigation-aliases?${params}`, onSuccess);
  }

  // ---- Policies ----

  list(clientId: string, onSuccess: (policies: DataPolicyListItem[]) => void): void {
    this.http.get<DataPolicyListItem[]>(
      `${this.base}/${encodeURIComponent(clientId)}/policies`,
      onSuccess
    );
  }

  get(clientId: string, policyId: string, onSuccess: (policy: DataPolicyDetail) => void): void {
    this.http.get<DataPolicyDetail>(
      `${this.base}/${encodeURIComponent(clientId)}/policies/${encodeURIComponent(policyId)}`,
      onSuccess
    );
  }

  create(clientId: string, request: CreateDataPolicyRequest, onSuccess: (id: string) => void): void {
    this.http.post(
      `${this.base}/${encodeURIComponent(clientId)}/policies`,
      (res: IdResult) => onSuccess(res?.id),
      null,
      request
    );
  }

  updatePolicy(clientId: string, request: UpdateDataPolicyRequest, onSuccess: () => void): void {
    this.http.put(`${this.base}/${encodeURIComponent(clientId)}/policies`, onSuccess, null, request);
  }

  setEnabled(clientId: string, policyId: string, enabled: boolean, onSuccess: () => void): void {
    const action = enabled ? 'enable' : 'disable';
    this.http.post(
      `${this.base}/${encodeURIComponent(clientId)}/policies/${encodeURIComponent(policyId)}/${action}`,
      onSuccess
    );
  }

  // ---- Tables ----

  addTable(clientId: string, request: AddTablePolicyRequest, onSuccess: (id: string) => void): void {
    this.http.post(
      `${this.base}/${encodeURIComponent(clientId)}/tables`,
      (res: IdResult) => onSuccess(res?.id),
      null,
      request
    );
  }

  updateTable(clientId: string, request: UpdateTablePolicyRequest, onSuccess: () => void): void {
    this.http.put(`${this.base}/${encodeURIComponent(clientId)}/tables`, onSuccess, null, request);
  }

  removeTable(clientId: string, tablePolicyId: string, onSuccess: () => void): void {
    this.http.deleteReturn(
      `${this.base}/${encodeURIComponent(clientId)}/tables/${encodeURIComponent(tablePolicyId)}`,
      onSuccess
    );
  }

  // ---- Fields ----

  addField(clientId: string, request: AddFieldRequest, onSuccess: (id: string) => void): void {
    this.http.post(
      `${this.base}/${encodeURIComponent(clientId)}/fields`,
      (res: IdResult) => onSuccess(res?.id),
      null,
      request
    );
  }

  updateField(clientId: string, request: UpdateFieldRequest, onSuccess: () => void): void {
    this.http.put(`${this.base}/${encodeURIComponent(clientId)}/fields`, onSuccess, null, request);
  }

  removeField(clientId: string, fieldId: string, onSuccess: () => void): void {
    this.http.deleteReturn(
      `${this.base}/${encodeURIComponent(clientId)}/fields/${encodeURIComponent(fieldId)}`,
      onSuccess
    );
  }

  // ---- Navigations ----

  addNavigation(clientId: string, request: AddNavigationRequest, onSuccess: (id: string) => void): void {
    this.http.post(
      `${this.base}/${encodeURIComponent(clientId)}/navigations`,
      (res: IdResult) => onSuccess(res?.id),
      null,
      request
    );
  }

  updateNavigation(clientId: string, request: UpdateNavigationRequest, onSuccess: () => void): void {
    this.http.put(`${this.base}/${encodeURIComponent(clientId)}/navigations`, onSuccess, null, request);
  }

  removeNavigation(clientId: string, navigationId: string, onSuccess: () => void): void {
    this.http.deleteReturn(
      `${this.base}/${encodeURIComponent(clientId)}/navigations/${encodeURIComponent(navigationId)}`,
      onSuccess
    );
  }
}
